import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Phone, Mail, Clock, ArrowRight, MapPin } from 'lucide-react';
import { contactsAPI } from '../services/api';
import { toast } from 'sonner';
import Seo from '../components/Seo';

const Contact = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const subject = search.get('subject');
    const message = search.get('message');
    if (subject || message) {
      setFormData((prev) => ({ ...prev, subject: subject || prev.subject, message: message || prev.message }));
      setTimeout(() => {
        const el = document.getElementById('contact-form');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  }, [location.search]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error(t('contact.fillAll', 'Veuillez remplir tous les champs requis'));
      return;
    }
    setIsLoading(true);
    try {
      await contactsAPI.create(formData);
      toast.success(t('contact.success', 'Message envoyé avec succès !'));
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || t('contact.error', "Erreur lors de l'envoi."));
    } finally {
      setIsLoading(false);
    }
  };

  const contactCards = [
    { icon: MapPin, title: t('contact.location', 'Adresse'), content: t('location.address', 'Wavre, Belgique') },
    { icon: Phone, title: t('contact.phone', 'Téléphone'), content: '+32 489 43 21 61', href: 'tel:+32489432161' },
    { icon: Mail, title: t('contact.email', 'Email'), content: 'info@autodistrict.be', href: 'mailto:info@autodistrict.be' },
    { icon: Clock, title: t('contact.hours', 'Horaires'), content: t('contact.appointmentOnly', 'Uniquement sur rendez-vous'), accent: true },
  ];

  return (
    <div className="min-h-screen bg-[#F7F8FA] pt-24 lg:pt-28 pb-20" data-testid="contact-page">
      <Seo title={t('seo.contact.title')} description={t('seo.contact.desc')} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <span className="section-eyebrow">{t('contact.title', 'Contact')}</span>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900" data-testid="contact-heading" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('contact.touch', 'Contactez-nous')}
          </h1>
          <p className="mt-3 text-slate-600 max-w-2xl">{t('contact.subtitle', 'Une question, une demande spécifique ? Notre équipe vous répond sous 24h ouvrées.')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact cards */}
          <div className="lg:col-span-1 space-y-4">
            {contactCards.map((card, i) => {
              const Icon = card.icon;
              const ContentEl = card.href ? 'a' : 'p';
              return (
                <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5 flex items-start gap-4 hover:shadow-md transition-shadow" data-testid={`contact-card-${i}`}>
                  <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 text-blue-900 flex items-center justify-center flex-shrink-0">
                    <Icon size={18} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-slate-900 text-sm font-semibold mb-1">{card.title}</h3>
                    <ContentEl
                      href={card.href}
                      className={`text-sm ${card.accent ? 'text-emerald-600 font-medium' : 'text-slate-600'} ${card.href ? 'hover:text-blue-900 transition-colors' : ''}`}
                    >
                      {card.content}
                    </ContentEl>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('contact.sendMessage', 'Envoyer un message')}
              </h2>

              <form id="contact-form" onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="form-label">{t('booking.fullName', 'Nom complet')} *</label>
                    <input name="name" value={formData.name} onChange={handleInputChange} placeholder="Jean Dupont" className="form-input" required data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label className="form-label">{t('booking.email', 'Email')} *</label>
                    <input name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="jean@example.com" className="form-input" required data-testid="contact-email-input" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="form-label">{t('booking.phone', 'Téléphone')}</label>
                    <input name="phone" type="tel" value={formData.phone} onChange={handleInputChange} placeholder="+32 489 12 34 56" className="form-input" data-testid="contact-phone-input" />
                  </div>
                  <div>
                    <label className="form-label">{t('contact.subject', 'Sujet')}</label>
                    <input name="subject" value={formData.subject} onChange={handleInputChange} placeholder={t('contact.howCanWeHelp', 'Comment pouvons-nous vous aider ?')} className="form-input" data-testid="contact-subject-input" />
                  </div>
                </div>

                <div>
                  <label className="form-label">{t('booking.message', 'Message')} *</label>
                  <textarea
                    name="message" value={formData.message} onChange={handleInputChange}
                    placeholder={t('contact.yourMessage', 'Votre message…')}
                    rows={5}
                    className="form-input resize-none" required data-testid="contact-message-input"
                  />
                </div>

                <button type="submit" className="btn-primary" disabled={isLoading} data-testid="contact-submit-btn">
                  {isLoading ? t('contact.sending', 'Envoi…') : t('contact.send', 'Envoyer le message')}
                  <ArrowRight size={14} />
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="mt-14" data-testid="contact-map-section">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-5">
            <div>
              <span className="section-eyebrow">{t('location.title', 'Notre adresse')}</span>
              <h3 className="mt-3 text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Wavre, Belgique</h3>
            </div>
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=1301+Wavre+Belgium"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary text-sm"
              data-testid="contact-directions-link"
            >
              {t('location.directions', 'Itinéraire')}
              <ArrowRight size={14} />
            </a>
          </div>
          <div className="relative aspect-[16/9] md:aspect-[21/9] rounded-2xl border border-slate-200 overflow-hidden">
            <iframe
              title="AutoDistrict Wavre"
              src="https://www.google.com/maps?q=1301+Wavre+Belgium&output=embed"
              className="absolute inset-0 w-full h-full"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map-iframe"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
